/**
 * CompactCard - Small status card for admin dashboard
 * Phase 11.5a - Admin Foundations
 */
import React from 'react'
import { IoTrendingUp, IoTrendingDown, IoRemove } from 'react-icons/io5'

type StatusVariant = 'healthy' | 'warning' | 'error' | 'neutral'
type TrendDirection = 'up' | 'down' | 'flat'

interface CompactCardProps {
  label: string
  value: string | number
  status?: StatusVariant
  trend?: string
  trendDirection?: TrendDirection
  icon?: React.ReactNode
  onClick?: () => void
  className?: string
}

export function CompactCard({
  label,
  value,
  status = 'neutral',
  trend,
  trendDirection = 'flat',
  icon,
  onClick,
  className = '',
}: CompactCardProps) {
  const isClickable = !!onClick

  const TrendIcon =
    trendDirection === 'up'
      ? IoTrendingUp
      : trendDirection === 'down'
        ? IoTrendingDown
        : IoRemove

  return (
    <div
      className={`admin-compact-card ${status} ${isClickable ? 'clickable' : ''} ${className}`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
    >
      {icon && <div className="admin-compact-card-icon">{icon}</div>}
      <div className="admin-compact-card-content">
        <div className="admin-compact-card-label">{label}</div>
        <div className="admin-compact-card-value">{value}</div>
        {trend && (
          <div className={`admin-compact-card-trend ${trendDirection}`}>
            <TrendIcon size={12} />
            <span>{trend}</span>
          </div>
        )}
      </div>
    </div>
  )
}

interface CompactCardGridProps {
  children: React.ReactNode
  columns?: 2 | 3 | 4
  className?: string
}

export function CompactCardGrid({
  children,
  columns = 3,
  className = '',
}: CompactCardGridProps) {
  return (
    <div className={`admin-compact-card-grid cols-${columns} ${className}`}>
      {children}
    </div>
  )
}
